import { FieldMeta } from '../../../types/webchatTabsTypes';
import { SectionMeta } from './types';

export const S3_FIELD_KEY = 'data_lake_s3_connection_id';

export const SECTION_META: SectionMeta[] = [
  { id: 'excel-export', heading: 'Excel Export', subtitle: 'Where large query results are exported and when an export is triggered.' },
  { id: 'sessions-memory', heading: 'Sessions & Memory', subtitle: 'Memory limits and lifetime of the in-memory Data Lake sessions.' },
  { id: 'autoload', heading: 'Autoload', subtitle: 'When tool results already in conversation history are loaded into the Data Lake.' },
  { id: 'analyze-data', heading: 'Analyze_Data', subtitle: 'How Analyze_Data query results are compacted before they are sent to the LLM.' },
];

export const FIELDS_BY_SECTION: Record<string, FieldMeta[]> = {
  'excel-export': [
    {
      key: S3_FIELD_KEY,
      label: 'S3 Connection',
      description: 'S3 connection used to store auto-exported Excel files. Only active S3 connections are listed.',
      details: 'Excel exports are stored in this S3 connection. Configure S3 connections on the Connections page. With no connection selected, Excel export is disabled.',
      type: 'select',
      fallbackDefault: '',
    },
    {
      key: 'data_lake_export_row_threshold',
      label: 'Export Row Threshold',
      description: 'Query results with more rows than this are automatically exported to Excel instead of returned inline.',
      type: 'number',
      fallbackDefault: 100,
      min: 1,
      step: 1,
    },
  ],
  'sessions-memory': [
    {
      key: 'data_lake_per_session_memory_bytes',
      label: 'Max Memory per Session (bytes)',
      description: "Maximum RAM one session's Data Lake may use. Default: 256 MB.",
      type: 'number',
      fallbackDefault: 268435456,
      min: 1,
      step: 1048576,
    },
    {
      key: 'data_lake_per_tenant_memory_bytes',
      label: 'Max Total Memory (bytes)',
      description: 'Maximum total RAM across all active sessions. Default: 1 GB.',
      type: 'number',
      fallbackDefault: 1073741824,
      min: 1,
      step: 1048576,
    },
    {
      key: 'data_lake_session_ttl_seconds',
      label: 'Session TTL (seconds)',
      description: 'Idle time before a session is evicted. Default: 1800 (30 minutes).',
      type: 'number',
      fallbackDefault: 1800,
      min: 60,
      step: 60,
    },
    {
      key: 'data_lake_max_sessions',
      label: 'Max Concurrent Sessions',
      description: 'Maximum number of concurrent Data Lake sessions.',
      type: 'number',
      fallbackDefault: 10,
      min: 1,
      step: 1,
    },
  ],
  autoload: [
    {
      key: 'data_lake_autoload_row_threshold',
      label: 'Autoload Row Threshold',
      description: 'Minimum row count before a webchat tool result already in conversation history is auto-loaded into the Data Lake.',
      details: 'Either this or the character threshold being met is enough to trigger the load.',
      type: 'number',
      fallbackDefault: 50,
      min: 1,
      step: 1,
    },
    {
      key: 'data_lake_autoload_char_threshold',
      label: 'Autoload Character Threshold',
      description: 'Minimum serialized character count before a webchat tool result already in conversation history is auto-loaded into the Data Lake.',
      details: 'Either this or the row threshold being met is enough to trigger the load.',
      type: 'number',
      fallbackDefault: 3000,
      min: 1,
      step: 100,
    },
    {
      key: 'data_lake_preview_rows',
      label: 'Preview Rows',
      description: 'Rows included as a preview in the compact dataset reference sent to the LLM when a tool result is auto-loaded.',
      details: "Covers other tools' auto-loaded results. Analyze_Data query results have their own preview setting in the Analyze_Data section.",
      type: 'number',
      fallbackDefault: 2,
      min: 0,
      step: 1,
    },
  ],
  'analyze-data': [
    {
      key: 'data_lake_compact_analyze_data_results',
      label: 'Compact Results',
      description: 'Compact Analyze_Data results between the autoload and Excel-export thresholds to a preview for the LLM.',
      details: 'The end user still sees full rows. Disable to always send full rows to the LLM.',
      type: 'boolean',
      fallbackDefault: 'true',
    },
    {
      key: 'data_lake_analyze_data_preview_rows',
      label: 'Preview Rows',
      description: "Rows included as a preview when an Analyze_Data query's own result is compacted for the LLM.",
      details: "Separate from the Autoload Preview Rows, which covers other tools' auto-loaded results.",
      type: 'number',
      fallbackDefault: 10,
      min: 0,
      step: 1,
      dependsOn: 'data_lake_compact_analyze_data_results',
      disabledNote: 'Disabled while Compact Results is off — full rows are always sent.',
    },
    {
      key: 'data_lake_analyze_data_full_rows_max_per_turn',
      label: 'Full-Row Requests per Turn',
      description: 'Maximum Analyze_Data calls per user turn that may use return_full_rows: true to bypass compaction.',
      details: 'Further requests in the same turn fall back to the standard preview.',
      type: 'number',
      fallbackDefault: 3,
      min: 0,
      step: 1,
      dependsOn: 'data_lake_compact_analyze_data_results',
      disabledNote: 'Disabled while Compact Results is off — nothing is compacted, so there is nothing to bypass.',
    },
  ],
};

export const ALL_FIELDS: FieldMeta[] = Object.values(FIELDS_BY_SECTION).flat();
